import { useState } from "react";
import { prepareReportProcessingData } from "../utils/reportProcessingUtils";

/**
 * Shared hook for report processing across PatientPage, EditableDocumentPage, and SchedulesTable.
 *
 * @param {function} onRun - Called when the user clicks Run in the ProcessReportPromptEditor.
 *   Receives: { fullPrompt, currentReportFullText, currentReportFileName, currentReportType, currentReportFileNames }
 * @param {function} toast - Chakra UI toast function
 */
export const useReportProcessing = ({ onRun, toast }) => {
  const [isReportPromptEditorOpen, setIsReportPromptEditorOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState(null);
  const [reportPromptEditorInitialData, setReportPromptEditorInitialData] = useState(null);
  const [currentReportFilePath, setCurrentReportFilePath] = useState(null);
  const [currentReportFileName, setCurrentReportFileName] = useState(null);
  const [currentReportFullText, setCurrentReportFullText] = useState(null);
  const [currentReportFileNames, setCurrentReportFileNames] = useState(null);

  const handleProcessReport = async (filePath, fileName, reportType, fullText, fileNames) => {
    console.log("📄 useReportProcessing: handleProcessReport called", {
      filePath,
      fileName,
      reportType,
      fileNamesCount: fileNames?.length ?? 0,
    });
    try {
      const initialData = await prepareReportProcessingData(reportType, fileName);
      setCurrentReportType(reportType);
      setCurrentReportFilePath(filePath);
      setCurrentReportFileName(fileName);
      setCurrentReportFullText(fullText || null);
      setCurrentReportFileNames(fileNames || null);
      setReportPromptEditorInitialData(initialData);
      setIsReportPromptEditorOpen(true);
    } catch (error) {
      console.error("❌ useReportProcessing: Error preparing report prompt:", error);
      toast({
        title: "Error",
        description: `Failed to prepare report prompt: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Called when the user clicks Run in ProcessReportPromptEditor.
   * Accepts optional overrides for fullText and fileNames to avoid React state batching
   * race conditions (e.g. when PatientPage prefills from SchedulesTable navigation state).
   */
  const handleReportProcessingRun = ({
    fullPrompt,
    fullTextOverride,
    fileNamesOverride,
  }) => {
    const effectiveFullText = fullTextOverride !== undefined ? fullTextOverride : currentReportFullText;
    const effectiveFileNames = fileNamesOverride !== undefined ? fileNamesOverride : currentReportFileNames;

    console.log("🔍 useReportProcessing: Running Process Report", {
      promptLength: fullPrompt.length,
      reportType: currentReportType,
      fileName: currentReportFileName,
      fileNames: effectiveFileNames,
      hasFullText: !!effectiveFullText,
      fullTextLength: effectiveFullText?.length ?? 0,
    });

    onRun({
      fullPrompt,
      currentReportFullText: effectiveFullText,
      currentReportFileName,
      currentReportType,
      currentReportFileNames: effectiveFileNames,
    });
  };

  return {
    // State
    isReportPromptEditorOpen,
    setIsReportPromptEditorOpen,
    currentReportType,
    reportPromptEditorInitialData,
    // Handlers
    handleProcessReport,
    handleReportProcessingRun,
  };
};
